import { useState } from 'react';
import Modal from './Modal';
import SignInWithGoogleButton from './SignInWithGoogleButton';
import SignInWithEmailButton from './SignInWithEmailButton';
import useAuth from '../contexts/useAuth';
import { startCheckout } from '../stripe';

type LimitModalType = 'signin' | 'upgrade' | 'pdf';

interface LimitModalProps {
  type: LimitModalType;
  onDismiss: () => void;
}

// Detect in-app browsers (Twitter, Facebook, Instagram, etc.)
function isInAppBrowser(): boolean {
  const ua = navigator.userAgent || navigator.vendor;
  return /FBAN|FBAV|Instagram|Twitter|Line|Snapchat|WeChat|MicroMessenger/i.test(
    ua
  );
}

const styles = {
  container: {
    maxWidth: 440,
    padding: '8px 0',
  } as React.CSSProperties,

  // Header section
  eyebrow: {
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: 'var(--accent)',
    marginBottom: 8,
  } as React.CSSProperties,

  headline: {
    fontFamily: 'var(--font-display)',
    fontSize: 26,
    fontWeight: 400,
    color: 'var(--primary)',
    margin: '0 0 6px 0',
    lineHeight: 1.15,
    letterSpacing: '-0.01em',
  } as React.CSSProperties,

  subhead: {
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    color: 'var(--primary-soft)',
    margin: '0 0 24px 0',
    lineHeight: 1.5,
  } as React.CSSProperties,

  // Plan cards
  plansContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
    marginBottom: 20,
  } as React.CSSProperties,

  planCard: {
    position: 'relative' as const,
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '16px 18px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: 'var(--card-bg)',
  } as React.CSSProperties,

  planCardSelected: {
    borderColor: 'var(--teal)',
    background: 'rgba(var(--teal-rgb), 0.04)',
    boxShadow: '0 0 0 1px var(--teal)',
  } as React.CSSProperties,

  planCardPro: {
    borderColor: 'var(--accent)',
    background:
      'linear-gradient(135deg, rgba(var(--gold-rgb), 0.06) 0%, rgba(var(--teal-rgb), 0.04) 100%)',
  } as React.CSSProperties,

  planCardProSelected: {
    boxShadow: '0 0 0 1px var(--accent)',
  } as React.CSSProperties,

  planHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  } as React.CSSProperties,

  planName: {
    fontFamily: 'var(--font-display)',
    fontSize: 18,
    fontWeight: 500,
    color: 'var(--primary)',
    margin: 0,
  } as React.CSSProperties,

  planBadge: {
    fontFamily: 'var(--font-body)',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    color: 'var(--card-bg)',
    background: 'var(--accent)',
    padding: '3px 8px',
    borderRadius: 4,
  } as React.CSSProperties,

  planPrice: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--primary)',
    margin: 0,
  } as React.CSSProperties,

  planPriceAmount: {
    fontWeight: 600,
    fontSize: 20,
  } as React.CSSProperties,

  planDescription: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--primary-soft)',
    margin: 0,
    lineHeight: 1.45,
    // Hidden on mobile via media query in component
  } as React.CSSProperties,

  planFeatures: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 6,
    marginTop: 10,
  } as React.CSSProperties,

  featureTag: {
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    color: 'var(--primary-soft)',
    background: 'rgba(0,0,0,0.04)',
    padding: '3px 8px',
    borderRadius: 4,
  } as React.CSSProperties,

  // Radio indicator
  radioOuter: {
    position: 'absolute' as const,
    right: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: '2px solid var(--border-strong)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  } as React.CSSProperties,

  radioOuterSelected: {
    borderColor: 'var(--teal)',
  } as React.CSSProperties,

  radioInner: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: 'var(--teal)',
    transform: 'scale(0)',
    transition: 'transform 0.15s ease',
  } as React.CSSProperties,

  radioInnerSelected: {
    transform: 'scale(1)',
  } as React.CSSProperties,

  // CTA button
  ctaButton: {
    width: '100%',
    height: 48,
    borderRadius: 10,
    border: 'none',
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: 'var(--teal)',
    color: 'var(--card-bg)',
  } as React.CSSProperties,

  ctaButtonPro: {
    background: 'linear-gradient(135deg, var(--accent) 0%, var(--teal) 100%)',
  } as React.CSSProperties,

  // Footer
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 16,
    paddingTop: 16,
    borderTop: '1px solid var(--border)',
  } as React.CSSProperties,

  dismissButton: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--primary-soft)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px 0',
    transition: 'color 0.2s ease',
  } as React.CSSProperties,

  currentPlan: {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: 'var(--primary-soft)',
    opacity: 0.8,
  } as React.CSSProperties,

  // Sign-in section
  signInContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
    marginBottom: 8,
    maxWidth: 220,
  } as React.CSSProperties,

  // Unified auth button style (matches Google button dimensions)
  authButton: {
    width: '100%',
    height: 40,
    borderRadius: 20,
    border: '1px solid var(--border-strong)',
    background: 'var(--card-bg)',
    color: 'var(--primary)',
    fontFamily: "'Roboto', arial, sans-serif",
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  } as React.CSSProperties,

  webviewWarning: {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: 'var(--accent)',
    background: 'rgba(var(--gold-rgb), 0.08)',
    padding: '10px 14px',
    borderRadius: 8,
    marginBottom: 16,
    lineHeight: 1.45,
  } as React.CSSProperties,

  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    margin: '6px 0',
  } as React.CSSProperties,

  dividerLine: {
    flex: 1,
    height: 1,
    background: 'var(--border)',
  } as React.CSSProperties,

  dividerText: {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: 'var(--primary-soft)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  } as React.CSSProperties,
};

type PlanTier = 'plus' | 'pro';

export default function LimitModal({ type, onDismiss }: LimitModalProps) {
  const { user } = useAuth();
  const inWebview = isInAppBrowser();
  const [selectedPlan, setSelectedPlan] = useState<PlanTier>('plus');
  const [upgrading, setUpgrading] = useState(false);

  const handleContinue = async () => {
    if (upgrading) return;

    // If user has an existing subscription, show confirmation for in-place upgrade
    const hasSubscription = user?.tier === 'plus' || user?.tier === 'pro';
    if (hasSubscription) {
      const priceText = selectedPlan === 'pro' ? '$20/month' : '$5/month';
      const confirmed = window.confirm(
        `Upgrade to ${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} (${priceText})?\n\nYou'll be charged the prorated difference for the rest of this billing period.`
      );
      if (!confirmed) return;
    }

    setUpgrading(true);
    const returnTo = window.location.pathname;
    const result = await startCheckout(selectedPlan, returnTo, true);

    if (result.error) {
      setUpgrading(false);
      alert(result.error);
    } else if (result.upgraded) {
      // Subscription was upgraded in-place, redirect to success page
      const params = new URLSearchParams({
        upgraded: 'true',
        tier: result.tier || selectedPlan,
        returnTo,
        wasPlaying: 'true',
      });
      window.location.href = `/sub-callback?${params.toString()}`;
    }
    // If result.url, user is being redirected to Stripe checkout
  };

  const currentTierLabel = () => {
    if (user?.tier === 'free') return 'Free · 5 min/day';
    if (user?.tier === 'plus') return 'Plus · 20 hrs/month';
    if (user?.tier === 'anonymous') return 'Preview mode';
    return null;
  };

  // Sign-in modal content
  if (type === 'signin') {
    return (
      <Modal onBackdropClick={onDismiss}>
        <div style={styles.container}>
          <div style={styles.eyebrow}>Login for unlimited listening</div>
          <h2 style={styles.headline}>Sign in to listen</h2>
          <p style={styles.subhead}>
            Earmark is free while we rebuild. Create an account to unlock
            playback, save your queue, and keep your progress synced.
          </p>

          {inWebview && (
            <div style={styles.webviewWarning}>
              In-app browsers can block sign-in. For the smoothest experience,
              open this page in Safari or Chrome.
            </div>
          )}

          <div style={styles.signInContainer}>
            {!inWebview && <SignInWithGoogleButton wasPlaying />}
            <SignInWithEmailButton wasPlaying buttonStyle={styles.authButton} />
            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>or</span>
              <div style={styles.dividerLine} />
            </div>
            <button
              onClick={onDismiss}
              style={{
                ...styles.authButton,
                background: 'transparent',
              }}
            >
              Not now
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Upgrade modal content (upgrade or pdf)
  const isPdfContext = type === 'pdf';

  return (
    <Modal onBackdropClick={onDismiss}>
      <style>
        {`
          @media (max-width: 480px) {
            .plan-description { display: none; }
            .upgrade-subhead { display: none; }
          }
        `}
      </style>
      <div style={styles.container}>
        <div style={styles.eyebrow}>
          {isPdfContext ? 'PDF support is Pro-only' : 'You hit the daily limit'}
        </div>
        <h2 style={styles.headline}>
          {isPdfContext
            ? 'Unlock PDFs &\nunlimited listening'
            : 'Upgrade for\nmore listening time'}
        </h2>
        <p className="upgrade-subhead" style={styles.subhead}>
          {isPdfContext
            ? 'Pro members get PDF parsing plus unlimited audio. Perfect for research papers, reports, and documents.'
            : 'Pick a plan that fits how you listen. Cancel anytime.'}
        </p>

        <div style={styles.plansContainer}>
          {/* Plus Plan */}
          <div
            style={{
              ...styles.planCard,
              ...(selectedPlan === 'plus' ? styles.planCardSelected : {}),
              paddingRight: 48,
            }}
            onClick={() => setSelectedPlan('plus')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setSelectedPlan('plus')}
          >
            <div style={styles.planHeader}>
              <div>
                <h3 style={styles.planName}>Plus</h3>
                <p style={styles.planPrice}>
                  <span style={styles.planPriceAmount}>$5</span> / month
                </p>
              </div>
            </div>
            <p className="plan-description" style={styles.planDescription}>
              20 hours of listening per month. Great for daily commutes and
              workouts.
            </p>
            <div style={styles.planFeatures}>
              <span style={styles.featureTag}>20 hrs/month</span>
              <span style={styles.featureTag}>Queue sync</span>
            </div>
            <div
              style={{
                ...styles.radioOuter,
                ...(selectedPlan === 'plus' ? styles.radioOuterSelected : {}),
              }}
            >
              <div
                style={{
                  ...styles.radioInner,
                  ...(selectedPlan === 'plus' ? styles.radioInnerSelected : {}),
                }}
              />
            </div>
          </div>

          {/* Pro Plan */}
          <div
            style={{
              ...styles.planCard,
              ...styles.planCardPro,
              ...(selectedPlan === 'pro' ? styles.planCardProSelected : {}),
              paddingRight: 48,
            }}
            onClick={() => setSelectedPlan('pro')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setSelectedPlan('pro')}
          >
            <div style={styles.planHeader}>
              <div>
                <h3 style={styles.planName}>Pro</h3>
                <p style={styles.planPrice}>
                  <span style={styles.planPriceAmount}>$20</span> / month
                </p>
              </div>
              <span style={styles.planBadge}>Best value</span>
            </div>
            <p className="plan-description" style={styles.planDescription}>
              Unlimited listening, PDF parsing, and beta access to new features.
            </p>
            <div style={styles.planFeatures}>
              <span style={styles.featureTag}>Unlimited</span>
              <span style={styles.featureTag}>PDF parsing</span>
              <span style={styles.featureTag}>Beta access</span>
            </div>
            <div
              style={{
                ...styles.radioOuter,
                ...(selectedPlan === 'pro'
                  ? {
                      ...styles.radioOuterSelected,
                      borderColor: 'var(--accent)',
                    }
                  : {}),
              }}
            >
              <div
                style={{
                  ...styles.radioInner,
                  background: 'var(--accent)',
                  ...(selectedPlan === 'pro' ? styles.radioInnerSelected : {}),
                }}
              />
            </div>
          </div>
        </div>

        <button
          onClick={handleContinue}
          disabled={upgrading}
          style={{
            ...styles.ctaButton,
            ...(selectedPlan === 'pro' ? styles.ctaButtonPro : {}),
            ...(upgrading ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
          }}
        >
          {upgrading
            ? 'Processing...'
            : `Continue with ${selectedPlan === 'plus' ? 'Plus' : 'Pro'}`}
        </button>

        <div style={styles.footer}>
          <button
            onClick={onDismiss}
            style={styles.dismissButton}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color = 'var(--primary)')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = 'var(--primary-soft)')
            }
          >
            Maybe later
          </button>
          {currentTierLabel() && (
            <span style={styles.currentPlan}>{currentTierLabel()}</span>
          )}
        </div>
      </div>
    </Modal>
  );
}
